import { outranks } from '@discordo/shared';
import type { Channel } from '@discordo/shared';
import { useEffect } from 'react';

import { watchForNewVersion } from '../lib/appVersion';
import { useCallback, useMemo, useRef, useState, type ReactElement } from 'react';

import { ChannelSidebar, type ChannelManagement } from '../components/ChannelSidebar';
import { ChatPanel, ChatToggle, useChatPanel } from '../components/ChatPanel';
import { GuildBar } from '../components/GuildBar';
import { MemberList, type MemberModeration } from '../components/MemberList';
import { VoiceRoom } from '../components/VoiceRoom';
import { reportCallState } from '../lib/desktopShell';
import { primeVoiceChime } from '../lib/voiceChime';
import { useAuth } from '../store/auth';
import { ChannelsProvider, useChannels } from '../store/channels';

/**
 * The signed-in app: servers, channels, the call, members — and the chat as a
 * panel the header button pulls in from the right.
 *
 * Almost all of the state comes from the channels store, and each column gets
 * exactly the slice it renders, which is what keeps the columns free of API
 * knowledge and testable on their own.
 *
 * Two pieces are the page's own. Which voice channel this session is connected
 * to belongs here and not in the store because a call outlives the channel
 * being read: you stay in voice while walking through text channels, exactly
 * as the sidebar shows the two selections apart. And whether the chat panel is
 * open, because the button that flips it sits in the conversation header while
 * the panel it flips is a column away.
 */

function typingNotice(names: string[]): string | null {
  const [first, second] = names;

  if (first === undefined) {
    return null;
  }
  if (second === undefined) {
    return `${first} está digitando…`;
  }
  if (names.length === 2) {
    return `${first} e ${second} estão digitando…`;
  }
  return 'Várias pessoas estão digitando…';
}

function AppShell(): ReactElement {
  const { user, logout, updateProfile } = useAuth();
  const {
    guilds,
    guild,
    selectGuild,
    createGuild,
    channels,
    categories,
    members,
    activeChannel,
    selectChannel,
    createChannel,
    updateChannel,
    deleteChannel,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderChannels,
    messages,
    hasMore,
    loading,
    loadingMessages,
    loadingOlder,
    loadOlder,
    sendMessage,
    sendAttachment,
    kickMember,
    banMember,
    listBans,
    revokeBan,
    notifyTyping,
    typingUserIds,
    onlineUserIds,
    voiceParticipants,
    error,
  } = useChannels();

  const [voiceChannelId, setVoiceChannelId] = useState<string | null>(null);
  const chat = useChatPanel(activeChannel?.id ?? null);

  /**
   * Clicking the channel already joined is how you hang up.
   *
   * The audio clock is started here, in the click itself, and that placement
   * is the whole reason this is not left to the call panel. A browser refuses
   * to make sound outside a gesture: an `AudioContext` built anywhere else
   * comes up suspended and then accepts every note without emitting one — no
   * exception, no warning, nothing in the console. Starting it on the click
   * that enters the call is the one moment the browser agrees to.
   */
  const toggleVoice = useCallback((channelId: string) => {
    primeVoiceChime();
    setVoiceChannelId((current) => (current === channelId ? null : channelId));
  }, []);

  /**
   * Switching rooms tears the old one down, and its disconnect arrives after
   * the new id is already set — so leaving only clears the channel that is
   * still the current one.
   */
  const leaveVoice = useCallback((channelId: string) => {
    setVoiceChannelId((current) => (current === channelId ? null : current));
  }, []);

  /**
   * The voice channel, held on to across channel-list reloads.
   *
   * Deriving it straight from `channels` made a call hostage to that list:
   * switching servers empties it for the length of a fetch, the lookup turns
   * to `null`, and the room below unmounts — dropping the call, renegotiating
   * ICE and rebuilding the jitter buffer, all because a sidebar was
   * refreshing. The last known channel is kept until the id itself changes,
   * which is the only event that should ever end a call.
   */
  const lastVoiceChannel = useRef<Channel | null>(null);

  const voiceChannel = useMemo(() => {
    if (voiceChannelId === null) {
      lastVoiceChannel.current = null;
      return null;
    }

    const found = channels.find((channel) => channel.id === voiceChannelId) ?? null;

    if (found !== null) {
      lastVoiceChannel.current = found;
      return found;
    }

    // Still the same channel, just a list that has not come back yet.
    return lastVoiceChannel.current?.id === voiceChannelId ? lastVoiceChannel.current : null;
  }, [channels, voiceChannelId]);

  /**
   * Applies a new deploy on its own, except during a call.
   *
   * The ref is what keeps the watcher from restarting every time somebody
   * joins or leaves a channel: restarting it would reset the fingerprint it
   * captured at load, and a watcher that keeps forgetting what it was
   * comparing against never sees a change.
   */
  const inCallRef = useRef(false);
  inCallRef.current = voiceChannelId !== null;

  useEffect(
    () =>
      watchForNewVersion({
        isInCall: () => inCallRef.current,
        reload: () => {
          window.location.reload();
        },
      }),
    [],
  );

  /**
   * O mesmo sinal, entregue à casca do Windows (§7 da Fase 6).
   *
   * Ela também se atualiza sozinha e também não pode fazer isso no meio de
   * uma chamada. Como a chamada acontece aqui, é daqui que sai a resposta —
   * senão cada camada teria a sua própria ideia de "ocupado". No navegador
   * não há ponte e isto não faz nada.
   */
  useEffect(() => {
    reportCallState(voiceChannelId !== null);
  }, [voiceChannelId]);

  /**
   * The channel and category actions, granted only to an owner or an admin —
   * which is also who `POST /api/channels` and its neighbours accept. Handing
   * an ordinary member `null` is what keeps the sidebar from offering buttons
   * that would only ever come back as `403`; it is not what enforces the rule,
   * which lives in the API and answers the same whether or not a button was
   * ever drawn.
   */
  const manage = useMemo<ChannelManagement | null>(() => {
    if (guild === null || guild.role === 'member') {
      return null;
    }

    return {
      createChannel,
      updateChannel,
      deleteChannel,
      createCategory,
      updateCategory,
      deleteCategory,
      reorderChannels,
    };
  }, [
    guild,
    createChannel,
    updateChannel,
    deleteChannel,
    createCategory,
    updateCategory,
    deleteCategory,
    reorderChannels,
  ]);

  /**
   * Expulsar e banir, oferecidos pela mesma regra de hierarquia que a API
   * aplica — `outranks`, importada do contrato compartilhado, para que a
   * pergunta tenha uma resposta só.
   *
   * Ninguém se alcança: sair do servidor é outra coisa, e é a pessoa quem faz.
   * O membro comum não recebe nada, o que também vale para a tela de banidos —
   * `GET /api/guilds/:id/bans` recusa qualquer um que não administre o
   * servidor, tenha o botão sido desenhado ou não.
   */
  const moderation = useMemo<MemberModeration | null>(() => {
    if (guild === null || guild.role === 'member' || !user) {
      return null;
    }

    const role = guild.role;
    return {
      canModerate: (member) => member.user.id !== user.id && outranks(role, member.role),
      kick: kickMember,
      ban: banMember,
      listBans,
      revokeBan,
    };
  }, [guild, user, kickMember, banMember, listBans, revokeBan]);

  const typingNames = useMemo(
    () =>
      typingUserIds
        .filter((id) => id !== user?.id)
        .map((id) => {
          const member = members.find((item) => item.user.id === id);
          return member?.nickname ?? member?.user.displayName ?? 'Alguém';
        }),
    [typingUserIds, members, user],
  );

  // The router only renders this page for a signed-in visitor; the guard makes
  // that promise legible to the type checker.
  if (!user) {
    return <main className="app-shell" />;
  }

  if (loading) {
    return (
      <main className="auth-screen">
        <p className="auth-subtitle">Carregando o servidor…</p>
      </main>
    );
  }

  /**
   * Belonging to no server at all stopped being impossible the moment servers
   * became many. The screen for it keeps the bar — and with it the `+` that is
   * the way out — instead of being a dead end that only a reload leaves.
   */
  if (guild === null) {
    return (
      <main className="app-shell is-empty">
        <GuildBar
          guilds={guilds}
          activeGuildId={null}
          onSelect={selectGuild}
          onCreate={createGuild}
        />
        <section className="conversation">
          <p className="conversation-empty">
            {error ?? 'Você ainda não faz parte de nenhum servidor. Crie um no + ao lado.'}
          </p>
        </section>
      </main>
    );
  }

  if (error !== null && activeChannel === null) {
    return (
      <main className="auth-screen">
        <p className="auth-alert" role="alert">
          {error}
        </p>
      </main>
    );
  }

  const notice = typingNotice(typingNames);
  const chatOpen = activeChannel !== null && chat.open;

  return (
    <main className={chatOpen ? 'app-shell has-chat' : 'app-shell'}>
      {/*
        The servers come before everything else, and switching one is what
        replaces the three columns to its right. One application serves them
        all: what changes here is which row is read, never which code runs.
      */}
      <GuildBar
        guilds={guilds}
        activeGuildId={guild.id}
        onSelect={selectGuild}
        onCreate={createGuild}
      />

      <ChannelSidebar
        guildName={guild.name}
        guildId={guild.id}
        channels={channels}
        categories={categories}
        activeChannelId={activeChannel?.id ?? null}
        onSelectChannel={selectChannel}
        activeVoiceChannelId={voiceChannel?.id ?? null}
        onJoinVoice={toggleVoice}
        voiceParticipants={voiceParticipants}
        user={user}
        onUpdateProfile={updateProfile}
        onLogout={() => {
          void logout();
        }}
        // Only the owner and the admins may cut a code, which is also the rule
        // `POST /api/invites` enforces.
        // Every member invites. The sidebar mirrors what the API allows;
        // hiding the button from members would only hide a permission they
        // have, and they would still be able to use it through the API.
        canInvite={true}
        manage={manage}
      />

      <section className="conversation">
        <header className="conversation-header">
          <h1 className="conversation-title">
            <span className="conversation-sigil" aria-hidden="true">
              #
            </span>
            {activeChannel?.name ?? 'sem canal'}
          </h1>
          {activeChannel !== null && <ChatToggle open={chat.open} onToggle={chat.toggle} />}
          {activeChannel?.topic !== null && activeChannel?.topic !== undefined && (
            <p className="conversation-topic">{activeChannel.topic}</p>
          )}
        </header>

        {/*
          The call now owns the whole column below the header, which is the
          point of the move: a shared screen is read at the height of the
          window instead of splitting it with the history.
        */}
        <div className="voice-slot">
          {voiceChannel !== null ? (
            <VoiceRoom
              // Re-keyed per channel so switching rooms builds a fresh
              // connection with the token that channel's id was issued for.
              key={voiceChannel.id}
              channelId={voiceChannel.id}
              channelName={voiceChannel.name}
              onLeave={() => {
                leaveVoice(voiceChannel.id);
              }}
            />
          ) : (
            <p className="conversation-empty">
              {activeChannel === null
                ? 'Nenhum canal de texto disponível neste servidor.'
                : 'Entre em um canal de voz para falar e compartilhar a tela.'}
            </p>
          )}
        </div>
      </section>

      <MemberList members={members} onlineUserIds={onlineUserIds} moderation={moderation} />

      {chatOpen && activeChannel !== null && (
        <ChatPanel
          channelName={activeChannel.name}
          messages={messages}
          hasMore={hasMore}
          loadingMessages={loadingMessages}
          loadingOlder={loadingOlder}
          onLoadOlder={loadOlder}
          onSend={sendMessage}
          onTyping={notifyTyping}
          typingNotice={notice}
          onSendAttachment={sendAttachment}
          onClose={chat.close}
        />
      )}
    </main>
  );
}

export function AppPage(): ReactElement {
  return (
    <ChannelsProvider>
      <AppShell />
    </ChannelsProvider>
  );
}

export default AppPage;
